import { useEffect } from "react";
import { Link } from "react-router-dom";
import "../styles/eitc-landing.css";

const SUPPORT_EMAIL = "support@mysportx.org";

/* Real EitC images from their public CDN */
const HERO_IMG = "https://images.webapi.gc.eitcservices.co.uk/fit-in/1800x1800/19e2fbb0-2904-11f1-ae37-45a92e746550.png";
const FEATURE_IMG = "https://images.webapi.gc.eitcservices.co.uk/fit-in/1800x1800/d51ba870-288f-11f1-bb61-c933dc152904.jpg";
const EITC_CREST = "https://images.webapi.gc.eitcservices.co.uk/fit-in/400x400/d7105d30-8658-11eb-aaaf-cb3bd8dd1ca9.png";

export default function EitCLanding() {
  useEffect(() => {
    document.title = "Sport X — Everton in the Community";
    return () => { document.title = "Sport X"; };
  }, []);

  return (
    <div className="eitc-landing" data-testid="eitc-landing-page">

      {/* ===== DRAFT WATERMARK ===== */}
      <div className="eitc-draft-bar" data-testid="eitc-draft-bar">
        Concept Mockup — Sport X for Everton in the Community — Not Final Branding
      </div>

      {/* ===== HERO ===== */}
      <section className="eitc-hero" data-testid="eitc-hero">
        <div
          className="eitc-hero-bg"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="eitc-hero-inner">
          <nav className="eitc-nav">
            <div className="eitc-nav-left">
              <div className="eitc-nav-badge">
                <img
                  src={EITC_CREST}
                  alt="EitC"
                  className="eitc-nav-crest"
                />
                <div className="eitc-nav-text">
                  Everton in the Community
                  <small>Official Charity</small>
                </div>
              </div>
            </div>
            <div className="eitc-nav-sportx">
              <span>Powered by</span> Sport X
            </div>
          </nav>

          <div className="eitc-hero-body">
            <div className="eitc-hero-tag">
              <span className="eitc-dot"></span>
              Community Engagement Platform
            </div>

            <h1>
              Give your fans a way to <em>participate together</em> — every matchday
            </h1>

            <p className="eitc-hero-sub">
              Friends compete in prediction challenges around real Everton matches.
              Community grows. Fundraising happens naturally. Zero gambling.
            </p>

            <div className="eitc-hero-pills">
              <div className="eitc-hero-pill">
                <span className="eitc-ic">&#10003;</span> Zero cost to EitC
              </div>
              <div className="eitc-hero-pill">
                <span className="eitc-ic">&#10003;</span> No gambling
              </div>
              <div className="eitc-hero-pill">
                <span className="eitc-ic">&#10003;</span> Live in 4 weeks
              </div>
              <div className="eitc-hero-pill">
                <span className="eitc-ic">&#10003;</span> 5 mins/match
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== IMPACT STRIP — real EitC stats ===== */}
      <div className="eitc-impact-strip" data-testid="eitc-impact-strip">
        <div className="eitc-impact-item">
          <div className="eitc-impact-num">162</div>
          <div className="eitc-impact-label">Delivery<br/>Venues</div>
        </div>
        <div className="eitc-impact-item">
          <div className="eitc-impact-num">89%</div>
          <div className="eitc-impact-label">Feel Engaged<br/>&amp; Inspired</div>
        </div>
        <div className="eitc-impact-item">
          <div className="eitc-impact-num">&pound;29.86</div>
          <div className="eitc-impact-label">Impact Per<br/>&pound;1 Invested</div>
        </div>
      </div>

      {/* ===== HOW IT WORKS ===== */}
      <section className="eitc-section" data-testid="eitc-how-it-works">
        <div className="eitc-section-label">How It Works</div>
        <h2>Simple for your team. Engaging for fans.</h2>
        <p className="eitc-section-intro">
          Runs alongside your existing fundraising. Pure addition — not replacement.
        </p>

        <div className="eitc-step-card">
          <div className="eitc-step-num">1</div>
          <div className="eitc-step-content">
            <h3>We brand it for EitC</h3>
            <p>
              Royal blue. Your identity. A custom Sport X experience that looks and feels
              like it belongs to Everton in the Community. Ready in 4 weeks.
            </p>
          </div>
        </div>

        <div className="eitc-step-card">
          <div className="eitc-step-num">2</div>
          <div className="eitc-step-content">
            <h3>Share the link on matchday</h3>
            <p>
              Post on social, email supporters, display at Hill Dickinson Stadium.
              Fans create groups with friends and make predictions around real Everton matches.
            </p>
          </div>
        </div>

        <div className="eitc-step-card">
          <div className="eitc-step-num">3</div>
          <div className="eitc-step-content">
            <h3>Community grows itself</h3>
            <p>
              Friends invite friends. Season leaderboards build loyalty.
              90% of every entry funds EitC programmes — mental health, youth justice,
              disability, education.
            </p>
          </div>
        </div>
      </section>

      {/* ===== THE EitC OPPORTUNITY ===== */}
      <section className="eitc-section" data-testid="eitc-opportunity">
        <div className="eitc-section-label">The Opportunity</div>
        <h2>Why this is built for EitC right now</h2>
        <p className="eitc-section-intro">
          The new stadium, new ownership, and 13,000 new fans create a unique moment.
        </p>

        <div className="eitc-diff-card eitc-feature">
          <div
            className="eitc-feature-bg"
            style={{ backgroundImage: `url(${FEATURE_IMG})` }}
          />
          <div className="eitc-diff-card-emoji">&#127963;&#65039;</div>
          <h4>The 13,000 new seats</h4>
          <p>
            Hill Dickinson Stadium brings 13,000+ new supporters who need a reason to connect
            with EitC — not just the club. Sport X turns every new ticket holder into a
            potential participant in your community mission.
          </p>
        </div>

        <div className="eitc-diff-card">
          <div className="eitc-diff-card-emoji">&#129309;</div>
          <h4>The "mass middle" supporter</h4>
          <p>
            The 1878 Club captures your most committed donors. The People's Lottery is passive
            income. Sport X reaches the thousands in between — fans who'll play a &pound;5 challenge
            with mates every match.
          </p>
        </div>

        <div className="eitc-diff-card">
          <div className="eitc-diff-card-emoji">&#9878;&#65039;</div>
          <h4>The ethical alternative</h4>
          <p>
            With gambling scrutiny growing, EitC can be the first Premier League charity to
            offer an official, zero-gambling fan engagement experience. Something you'd
            promote to a 15-year-old.
          </p>
        </div>

        <div className="eitc-diff-card">
          <div className="eitc-diff-card-emoji">&#128202;</div>
          <h4>Data that feeds Salesforce</h4>
          <p>
            Every participant is a data point — who they are, how often they play, who they
            invite. Feeds directly into your digital and data strategy alongside the club's CRM.
          </p>
        </div>

        <div className="eitc-diff-card">
          <div className="eitc-diff-card-emoji">&#128260;</div>
          <h4>19 home games, not one event</h4>
          <p>
            This isn't a sleepout or a golf day. It runs every matchday, all season. Ongoing
            engagement that compounds — and generates impact reporting data automatically.
          </p>
        </div>
      </section>

      {/* ===== COMPARISON TABLE ===== */}
      <section className="eitc-section" data-testid="eitc-comparison">
        <div className="eitc-section-label">Why It's Different</div>
        <h2>Not a lottery. Not a betting app.</h2>
        <p className="eitc-section-intro">
          How Sport X compares to what fans currently use.
        </p>

        {/* Header */}
        <div className="eitc-compare-row eitc-compare-header">
          <div className="eitc-compare-cell eitc-compare-cell-wide"></div>
          <div className="eitc-compare-cell">Betting apps</div>
          <div className="eitc-compare-cell">People's Lottery</div>
          <div className="eitc-compare-cell">Sport X</div>
        </div>

        {/* Rows */}
        <CompareRow label="Play with friends" betting="no" lottery="no" sportx="yes" />
        <CompareRow label="Zero gambling" betting="no" lottery="meh" sportx="yes" />
        <CompareRow label="EitC branded" betting="no" lottery="meh" sportx="yes" />
        <CompareRow label="Builds community" betting="no" lottery="no" sportx="yes" />
        <CompareRow label="Every matchday" betting="yes" lottery="no" sportx="yes" />
        <CompareRow label="Funds EitC" betting="no" lottery="yes" sportx="yes" last />
      </section>

      {/* ===== CTA ===== */}
      <section className="eitc-cta-section" data-testid="eitc-cta">
        <div className="eitc-cta-card">
          <h2>Pilot this season</h2>
          <p className="eitc-cta-sub">
            Remaining 2025/26 fixtures plus 2026/27 pre-season.
            We handle all technology. You share a link.
          </p>

          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=EitC%20Pilot%20Discussion`}
            className="eitc-btn-main"
            data-testid="eitc-cta-pilot-btn"
          >
            Let's Talk About A Pilot
          </a>

          <Link
            to="/"
            className="eitc-btn-ghost"
            data-testid="eitc-cta-platform-btn"
          >
            See How The Platform Works &rarr;
          </Link>

          <div className="eitc-cta-checks">
            <span className="eitc-cta-check">
              <span className="eitc-t">&#10003;</span> Zero cost to EitC
            </span>
            <span className="eitc-cta-check">
              <span className="eitc-t">&#10003;</span> 90% of contributions to your programmes
            </span>
            <span className="eitc-cta-check">
              <span className="eitc-t">&#10003;</span> No branding obligations or endorsements
            </span>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <div className="eitc-footer" data-testid="eitc-footer">
        <strong>Sport X</strong> is a product of Jana Sports Foundation.<br />
        Social. Safe. Sport. — <strong>Zero gambling. Always.</strong><br /><br />
        This is a concept mockup prepared for discussion purposes only.<br />
        Everton in the Community branding is used illustratively and is not an endorsement.
      </div>
    </div>
  );
}

/* ── Comparison Row helper ── */
function CompareRow({ label, betting, lottery, sportx, last }) {
  const icon = (val) => {
    if (val === "yes") return <span className="eitc-yes">&#10003;</span>;
    if (val === "no") return <span className="eitc-no">&#10007;</span>;
    return <span className="eitc-meh">~</span>;
  };

  return (
    <div className={`eitc-compare-row eitc-compare-data${last ? " eitc-compare-last" : ""}`}>
      <div className="eitc-compare-cell eitc-compare-cell-wide">{label}</div>
      <div className="eitc-compare-cell">{icon(betting)}</div>
      <div className="eitc-compare-cell">{icon(lottery)}</div>
      <div className="eitc-compare-cell">{icon(sportx)}</div>
    </div>
  );
}
